import { faDownload, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Button,
  Card,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";

// how many fixed header columns are always shown (left attribute column)
const FIXED_HEADER_COLS = 1;

function filterHeaderForBrands(headEl, fullBrandOrder = [], subsetBrands = []) {
  if (!headEl || !subsetBrands.length) return headEl;

  const keepBrandIdxs = subsetBrands
    .map((b) => fullBrandOrder.indexOf(b))
    .filter((i) => i >= 0);

  const headChildren = React.Children.toArray(headEl.props.children);
  if (!headChildren.length) return headEl;

  const rowEl = headChildren[0];
  const cells = React.Children.toArray(rowEl.props.children);

  const filteredCells = cells.filter((_, idx) => {
    if (idx < FIXED_HEADER_COLS) return true;
    const brandIdx = idx - FIXED_HEADER_COLS;
    return keepBrandIdxs.includes(brandIdx);
  });

  const newRow = React.cloneElement(rowEl, {}, filteredCells);
  return React.cloneElement(headEl, {}, newRow);
}

const DetailedTableModal = ({
  show,
  onClose,
  data,
  renderHeaders,
  formatName,
  classes,
  pdfHeight,
  pdfTitle,
  headers,
  showSplitTables = false,
}) => {
  // ---- flags / constants ----------------------------------------------------
  const search = typeof window !== "undefined" ? window.location.search : "";
  const reporting =
    typeof window !== "undefined" &&
    new URLSearchParams(search).get("isReporting") === "true";

  const normalizedTitle = (pdfTitle ?? "").toLowerCase();

  const grid = useMemo(() => {
    if (normalizedTitle.includes("brand pillars"))     return { rows: 4, cols: 5 };
    if (normalizedTitle.includes("purchase criteria")) return { rows: 5, cols: 5 };
    return { rows: 5, cols: 5 };
  }, [normalizedTitle]);

  const pdfRef = useRef();

  const maxCardDimensions = !reporting ? { width: 200, height: 135 } : { width: 260, height: 160 };
  const FONT_SIZE = !reporting ? "9.8px" : "14px";


  const printRef = useRef();

  const PRINT_COLS_PER_PAGE = 4;

  const exportToPDF = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${pdfTitle}`,
    removeAfterPrint: true,
    pageStyle: `
      @page {
        size: 380mm auto;
        margin-left:   10mm;
        margin-right:  10mm;
        margin-bottom: 5mm;
        margin-top:    15mm;
      }

      @media print {
        #print-pdf,
        .card-container {
          overflow:   visible !important;
          max-height: none    !important;
          height:     auto    !important;
          flex:       none    !important;
        }

        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust:         exact;
        }
      }
    `,
  });

  // ---- Precompute split halves (used only when reporting=false) -------------
  const half      = showSplitTables ? Math.ceil((data?.length || 0) / 2) : (data?.length || 0);
  const firstHalf  = useMemo(() => (data || []).slice(0, half),                       [data, half]);
  const secondHalf = useMemo(() => (showSplitTables ? (data || []).slice(half) : []), [data, half, showSplitTables]);

  // ---- Tiling setup (hooks always called, logic branches by `reporting`) ----
  const rowsPerPage    = grid.rows;
  const colsPerPage    = grid.cols;
  const totalRows      = Array.isArray(data)    ? data.length    : 0;
  const totalBrandCols = Array.isArray(headers) ? headers.length : 0;

  const windows = useMemo(() => {
    if (!reporting) {
      return [{ rowStart: 0, rowEnd: totalRows, colStart: 0, colEnd: totalBrandCols }];
    }
    const list = [];
    const rowBlocks = Math.ceil(totalRows      / rowsPerPage) || 1;
    const colBlocks = Math.ceil(totalBrandCols / colsPerPage) || 1;

    for (let cb = 0; cb < colBlocks; cb++) {
      const colStart = cb * colsPerPage;
      const colEnd   = Math.min(totalBrandCols, colStart + colsPerPage);
      for (let rb = 0; rb < rowBlocks; rb++) {
        const rowStart = rb * rowsPerPage;
        const rowEnd   = Math.min(totalRows, rowStart + rowsPerPage);
        list.push({ rowStart, rowEnd, colStart, colEnd });
      }
    }
    return list;
  }, [reporting, totalRows, totalBrandCols, rowsPerPage, colsPerPage]);

  const totalPages = windows.length || 1;
  const [pageIndex, setPageIndex] = useState(0);

  const { rowStart, rowEnd, colStart, colEnd } = windows[pageIndex] || {
    rowStart: 0, rowEnd: 0, colStart: 0, colEnd: 0,
  };

  const rowsToRender   = useMemo(() => (Array.isArray(data)    ? data.slice(rowStart, rowEnd)   : []), [data,    rowStart, rowEnd]);
  const brandsToRender = useMemo(() => (Array.isArray(headers) ? headers.slice(colStart, colEnd) : []), [headers, colStart, colEnd]);

  const fullHeaderEl = useMemo(
    () => (typeof renderHeaders === "function" ? renderHeaders() : null),
    [renderHeaders]
  );

  const headerEl = useMemo(() => {
    return reporting
      ? filterHeaderForBrands(fullHeaderEl, headers || [], brandsToRender || [])
      : fullHeaderEl;
  }, [reporting, fullHeaderEl, headers, brandsToRender]);

  const hasData =
    Array.isArray(data)    && data.length    > 0 &&
    Array.isArray(headers) && headers.length > 0;

  useEffect(() => {
    if (!reporting || !hasData || typeof window === "undefined") return;
    window.__REPORT_META   = { totalPages };
    window.__setReportPage = (i) => {
      const idx = Math.max(0, Math.min(totalPages - 1, Number(i) || 0));
      setPageIndex(idx);
    };
    setPageIndex(0);
    return () => {
      try { delete window.__REPORT_META;   } catch {}
      try { delete window.__setReportPage; } catch {}
    };
  }, [reporting, totalPages, hasData]);

  // =============================== RENDER ===================================
  console.log("Auditreporting", reporting, totalPages);
  return (
    <Modal open={show} onClose={onClose} id="metric_detail">
      <Box
        sx={{
          width:           reporting ? "95%" : "90%",
          maxWidth:        reporting ? "1750px" : "1400px",
          backgroundColor: "white",
          boxShadow:       24,
          p:               3,
          borderRadius:    2,
          position:        "absolute",
          top:             "5vh",
          left:            "50%",
          transform:       "translateX(-50%)",
          maxHeight:       "90vh",
          display:         "flex",
          flexDirection:   "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display:        "flex",
            justifyContent: "space-between",
            alignItems:     "center",
            mb:             2,
          }}
        >
          <Typography variant="h6">Detailed Table View </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {reporting && (
              <Typography variant="body2">
                Page {Math.min(pageIndex + 1, totalPages)} / {totalPages}
              </Typography>
            )}
            {reporting && totalPages > 1 && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  id="report-prev"
                  size="small"
                  variant="outlined"
                  disabled={pageIndex === 0}
                  onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                >
                  Prev
                </Button>
                <Button
                  id="report-next"
                  size="small"
                  variant="outlined"
                  disabled={pageIndex >= totalPages - 1}
                  onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
                >
                  Next
                </Button>
              </Box>
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={exportToPDF}
              sx={{ textTransform: "none" }}
              disabled={!hasData}
            >
              <FontAwesomeIcon icon={faDownload} style={{ marginRight: "5px" }} />
              Download as PDF
            </Button>
            <IconButton id="close_id" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          </Box>
        </Box>

        {/* Container: allow both scrolls in non-reporting; lock in reporting */}
        {!hasData ? (
          <Box
            sx={{
              height:         "100%",
              minHeight:      240,
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
              pb:             2,
            }}
          >
            <Typography>Data Not Available</Typography>
          </Box>
        ) : (
          <div
            style={{
              flex:          1,
              overflow:      reporting ? "hidden" : "auto",
              paddingBottom: 8,
            }}
            className="card-container"
            id="print-pdf"
            data-report-page={pageIndex}
            ref={pdfRef}
          >
            <Box sx={{ pb: 4 }}>
              <Table sx={{ tableLayout: "fixed" }}>
                {/* Fixed column widths so the header row (brand logo/name) always lines up
                    exactly with the data columns below, regardless of each column's own CSS class widths. */}
                <colgroup>
                  <col style={{ width: 150 }} />
                  {(reporting ? brandsToRender : headers || []).map((_, i) => (
                    <col key={i} style={{ width: maxCardDimensions.width + 24 }} />
                  ))}
                </colgroup>
                {/* reporting uses paged/filtered headers; normal mode uses full headers */}
                {headerEl}

                {/* Non-reporting + showSplitTables (alt/shift) keeps original behavior */}
                {!reporting && showSplitTables === "altKey" && secondHalf.length > 0 ? (
                  <TableBody>
                    {secondHalf.map((mainItem, index) => (
                      <TableRow key={index + half}>
                        <TableCell className={classes.attribute_headings}>
                          {formatName(mainItem.name)}
                        </TableCell>
                        {headers?.map((brand, idx) => {
                          const detailInfo = mainItem?.scores || mainItem?.brands;
                          const brandScore = detailInfo?.find((item) => item.name === brand);
                          const score = brandScore?.score || brandScore?.rating;
                          let color = "#dc3545";
                          const displayedScore = score ? Number(Number(score).toFixed(0)) : null;
                          if (displayedScore >= 7) color = "#2196F3";
                          if (displayedScore >= 9) color = "#198754";
                          return (
                            <TableCell key={idx} className={classes.attribute_score} sx={{ verticalAlign: "top" }}>
                              {brandScore ? (
                                <Card sx={{ width: maxCardDimensions.width, p: "8px 10px" }}>
                                  <Typography sx={{ padding: "1px", fontWeight: "bold", color }} variant="body1">
                                    {Number(score).toFixed(0)}
                                  </Typography>
                                  <Typography sx={{ fontSize: FONT_SIZE, mt: 0.4, lineHeight: 1.55 }}>
                                    {brandScore.explanation}
                                  </Typography>
                                </Card>
                              ) : (
                                <Typography>-</Typography>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>

                ) : !reporting && (!showSplitTables || showSplitTables === "shiftKey") ? (
                  <TableBody>
                    {firstHalf.map((mainItem, index) => (
                      <TableRow key={index}>
                        <TableCell className={classes.attribute_headings}>
                          {formatName(mainItem.name)}
                        </TableCell>
                        {headers?.map((brand, idx) => {
                          const detailInfo = mainItem?.scores || mainItem?.brands;
                          const brandScore = detailInfo?.find(
                            (item) => item.name?.toLowerCase() === brand?.toLowerCase()
                          );
                          const score = brandScore?.score || brandScore?.rating;
                          let color = "#dc3545";
                          const displayedScore = score ? Number(Number(score).toFixed(0)) : null;
                          if (displayedScore >= 7) color = "#2196F3";
                          if (displayedScore >= 9) color = "#198754";
                          return (
                            <TableCell key={idx} className={classes.attribute_score} sx={{ verticalAlign: "top" }}>
                              {score ? (
                                <Card sx={{ width: maxCardDimensions.width, p: "8px 10px" }}>
                                  <Typography sx={{ padding: "1px", fontWeight: "bold", color }} variant="body1">
                                    {Number(score).toFixed(0)}
                                  </Typography>
                                  <Typography sx={{ fontSize: FONT_SIZE, mt: 0.4, lineHeight: 1.55 }}>
                                    {brandScore.explanation}
                                  </Typography>
                                </Card>
                              ) : (
                                <Card sx={{ width: maxCardDimensions.width, minHeight: 40, display: "flex", justifyContent: "center", alignItems: "center" }}>
                                  <Typography>-</Typography>
                                </Card>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>

                ) : (
                  // reporting mode: tiled rendering using rowsToRender + brandsToRender
                  <TableBody>
                    {rowsToRender.map((mainItem, rIdx) => (
                      <TableRow key={`rep-${rowStart + rIdx}`}>
                        <TableCell className={classes.attribute_headings}>
                          {formatName(mainItem.name)}
                        </TableCell>
                        {brandsToRender.map((brand) => {
                          const detailInfo = mainItem?.scores || mainItem?.brands || [];
                          const brandScore = detailInfo?.find((item) => item?.name === brand);
                          const rawScore   = brandScore?.score ?? brandScore?.rating;
                          const scoreNum   = typeof rawScore === "number" ? rawScore : Number(rawScore);
                          const displayedScoreNum = scoreNum ? Number(scoreNum.toFixed(0)) : null;
                          let color = "#dc3545";
                          if (displayedScoreNum >= 7) color = "#2196F3";
                          if (displayedScoreNum >= 9) color = "#198754";
                          return (
                            <TableCell
                              key={`${mainItem.name}-${brand}`}
                              className={classes.attribute_score}
                            >
                              <Box sx={{ display: "flex", justifyContent: "center" }}>
                                {brandScore ? (
                                  <Card sx={{ width: maxCardDimensions.width, height: maxCardDimensions.height, p: 1 }}>
                                    <Typography sx={{ fontWeight: "bold", color }} variant="body1">
                                      {Number(scoreNum).toFixed(0)}
                                    </Typography>
                                    <Typography sx={{ fontSize: FONT_SIZE, mt: 0.2 }}>
                                      {brandScore.explanation}
                                    </Typography>
                                  </Card>
                                ) : (
                                  <Typography>-</Typography>
                                )}
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                )}
              </Table>
            </Box>
          </div>
        )}

        {hasData && (
          <span style={{ display: "none" }}>
          <div
            ref={printRef}
            style={{ background: "#ffffff", padding: "8px" }}
          >
            {Array.from({ length: Math.ceil((Array.isArray(headers) ? headers.length : 0) / PRINT_COLS_PER_PAGE) }).map((_, pageNum) => {
              const colStart = pageNum * PRINT_COLS_PER_PAGE;
              const colEnd = Math.min(Array.isArray(headers) ? headers.length : 0, colStart + PRINT_COLS_PER_PAGE);
              const pageBrands = (Array.isArray(headers) ? headers : []).slice(colStart, colEnd);
              const filteredHdr = filterHeaderForBrands(fullHeaderEl, headers || [], pageBrands);
              const isLastGroup = colEnd >= (Array.isArray(headers) ? headers.length : 0);
              return (
                <div
                  key={pageNum}
                  style={{ breakAfter: isLastGroup ? "auto" : "page", pageBreakAfter: isLastGroup ? "auto" : "always" }}
                >
                  <Table>
                    {filteredHdr}
                    <TableBody>
                      {(Array.isArray(data) ? data : []).map((mainItem, rIdx) => {
                        return (
                          <TableRow key={rIdx}>
                            <TableCell className={classes.attribute_headings}>
                              {formatName(mainItem.name)}
                            </TableCell>
                            {pageBrands.map((brand) => {
                              const detailInfo = mainItem?.scores || mainItem?.brands || [];
                              const brandScore = detailInfo?.find((item) => item?.name === brand);
                              const rawScore = brandScore?.score ?? brandScore?.rating;
                              const scoreNum = typeof rawScore === "number" ? rawScore : Number(rawScore);
                              const displayedScoreNum = scoreNum ? Number(scoreNum.toFixed(0)) : null;
                              let color = "#dc3545";
                              if (displayedScoreNum >= 7) color = "#2196F3";
                              if (displayedScoreNum >= 9) color = "#198754";
                              return (
                                <TableCell key={`${mainItem.name}-${brand}`} className={classes.attribute_score}>
                                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                                    {brandScore ? (
                                      <Card sx={{ width: maxCardDimensions.width, height: maxCardDimensions.height, p: 1 }}>
                                        <Typography sx={{ fontWeight: "bold", color }} variant="body1">
                                          {Number(scoreNum).toFixed(0)}
                                        </Typography>
                                        <Typography sx={{ fontSize: FONT_SIZE, mt: 0.2 }}>
                                          {brandScore.explanation}
                                        </Typography>
                                      </Card>
                                    ) : (
                                      <Typography>-</Typography>
                                    )}
                                  </Box>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              );
            })}
          </div>
          </span>
        )}
      </Box>
    </Modal>
  );
};

export function ReportScreenShot({ data, renderHeaders, formatName, classes, headers }) {
  const half              = Math.ceil(data.length / 2);
  const firstHalf         = data.slice(0, half);
  const secondHalf        = data.slice(half);
  const maxCardDimensions = { width: 200, height: 135 };

  return (
    <div id="brand_pillars_detail">
      {[firstHalf, secondHalf].map((ele, index) => (
        <div
          key={index}
          style={{ width: "100%", height: "100%", pb: 2, display: "none" }}
          id={`brand_pillars_detail_${index + 1}`}
        >
          <Box sx={{ paddingBottom: "40px" }}>
            <Table>
              {renderHeaders(true)}
              <TableBody>
                {ele.map((mainItem, i) => (
                  <TableRow key={i}>
                    <TableCell className={classes.attribute_headings}>
                      {formatName(mainItem.name)}
                    </TableCell>
                    {headers?.map((brand, idx) => {
                      const detailInfo = mainItem?.scores || mainItem?.brands;
                      const brandScore = detailInfo?.find((item) =>
                        item.name?.toLowerCase().includes(brand?.toLowerCase())
                      );
                      const score = brandScore?.score || brandScore?.rating;
                      const displayedScore = score ? Number(Number(score).toFixed(0)) : null;
                      let color = "#dc3545";
                      if (displayedScore >= 7) color = "#2196F3";
                      if (displayedScore >= 9) color = "#198754";
                      return (
                        <TableCell key={idx} className={classes.attribute_score}>
                          {brandScore ? (
                            <Card sx={{ width: maxCardDimensions.width, height: maxCardDimensions.height }}>
                              <Typography sx={{ padding: "1px", fontWeight: "bold", color }} variant="body1">
                                {Number(score).toFixed(0)}
                              </Typography>
                              <Typography sx={{ fontSize: "9.8px", mt: 0.2 }}>
                                {brandScore.explanation}
                              </Typography>
                            </Card>
                          ) : (
                            <Typography>-</Typography>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </div>
      ))}
    </div>
  );
}

export default DetailedTableModal;